import { useState } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus,
  Search,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ClipboardCheck,
  Filter,
  Eye,
  TrendingUp,
  Activity,
  BarChart3,
} from "lucide-react";
import { QualityInspectionDialog } from "@/components/Quality/QualityInspectionDialog";
import { DefectFormDialog } from "@/components/Quality/DefectFormDialog";
import { QualityInspectionDetails } from "@/components/Quality/QualityInspectionDetails";
import { QualityMetricsDashboard } from "@/components/Quality/QualityMetricsDashboard";
import { NCRList } from "@/components/Quality/NCRList";
import { format } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface QualityInspection {
  id: string;
  batch_id: string | null;
  work_order_id: string | null;
  inspector_id: string;
  inspection_type: string;
  inspection_date: string;
  status: string;
  defects_found: number;
  notes: string | null;
  work_orders?: {
    order_number: string;
    product_name: string;
    customer: string | null;
  };
  production_batches?: {
    batch_number: string;
    quantity: number;
  };
  profiles?: {
    full_name: string | null;
    email: string;
  };
}

export default function QualityControl() {
  const { userRole } = useAuth();
  const { language } = useLanguage();
  const locale = language === "es" ? es : enUS;
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [inspectionDialogOpen, setInspectionDialogOpen] = useState(false);
  const [defectDialogOpen, setDefectDialogOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState<string | null>(null);
  const [viewInspectionId, setViewInspectionId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("inspections");

  const { data: inspections, isLoading } = useQuery({
    queryKey: ["quality-inspections"],
    queryFn: async () => {
      const { data, error } = await api
        .from("quality_inspections")
        .select(`
          *,
          work_orders(order_number, product_name, customer),
          production_batches(batch_number, quantity),
          profiles(full_name, email)
        `)
        .order("inspection_date", { ascending: false });

      if (error) throw error;
      return data as QualityInspection[];
    },
  });

  const filteredInspections = inspections?.filter((inspection) => {
    const matchesSearch =
      searchTerm === "" ||
      inspection.work_orders?.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inspection.work_orders?.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inspection.production_batches?.batch_number?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === "all" || inspection.status === statusFilter;
    const matchesType = typeFilter === "all" || inspection.inspection_type === typeFilter;

    return matchesSearch && matchesStatus && matchesType;
  });

  const stats = {
    total: inspections?.length || 0,
    passed: inspections?.filter((i) => i.status === "passed").length || 0,
    failed: inspections?.filter((i) => i.status === "failed").length || 0,
    totalDefects: inspections?.reduce((sum, i) => sum + (i.defects_found || 0), 0) || 0,
    passRate: inspections?.length
      ? Math.round(
          ((inspections.filter((i) => i.status === "passed").length) /
            inspections.filter((i) => i.status === "passed" || i.status === "failed").length) *
            100
        ) || 0
      : 0,
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { label: string; color: string; icon: any }> = {
      pending: { label: "Pendiente", color: "bg-gray-500/10 text-gray-500", icon: AlertTriangle },
      in_progress: { label: "En Progreso", color: "bg-blue-500/10 text-blue-500", icon: Activity },
      passed: { label: "Aprobado", color: "bg-green-500/10 text-green-500", icon: CheckCircle2 },
      failed: { label: "Rechazado", color: "bg-red-500/10 text-red-500", icon: XCircle },
      conditional: { label: "Condicional", color: "bg-yellow-500/10 text-yellow-500", icon: AlertTriangle },
    };

    const { label, color, icon: Icon } = config[status] || config.pending;
    return (
      <Badge className={cn("gap-1", color)}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      incoming: "Recepcion",
      in_process: "En Proceso",
      final: "Final",
      random: "Aleatoria",
    };
    return labels[type] || type;
  };

  const canManage = userRole === "admin" || userRole === "supervisor" || userRole === "quality";

  if (viewInspectionId) {
    return (
      <DashboardLayout>
        <QualityInspectionDetails
          inspectionId={viewInspectionId}
          onBack={() => setViewInspectionId(null)}
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Control de Calidad</h1>
            <p className="text-muted-foreground mt-1">
              Gestion de inspecciones, defectos y metricas de calidad
            </p>
          </div>
          {canManage && (
            <Button onClick={() => setInspectionDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nueva Inspeccion
            </Button>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="inspections" className="flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4" />
              Inspecciones
            </TabsTrigger>
            <TabsTrigger value="ncr" className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              NCRs
            </TabsTrigger>
            <TabsTrigger value="metrics" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Metricas
            </TabsTrigger>
          </TabsList>

          <TabsContent value="inspections" className="mt-6 space-y-6">
            <div className="grid gap-4 md:grid-cols-5">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-500/10">
                      <ClipboardCheck className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.total}</p>
                      <p className="text-sm text-muted-foreground">Total</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-500/10">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.passed}</p>
                      <p className="text-sm text-muted-foreground">Aprobadas</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-red-500/10">
                      <XCircle className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.failed}</p>
                      <p className="text-sm text-muted-foreground">Rechazadas</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-yellow-500/10">
                      <AlertTriangle className="w-5 h-5 text-yellow-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.totalDefects}</p>
                      <p className="text-sm text-muted-foreground">Defectos</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-500/10">
                      <TrendingUp className="w-5 h-5 text-purple-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.passRate}%</p>
                      <p className="text-sm text-muted-foreground">Tasa Aprobacion</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por OT, producto o lote..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[160px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendientes</SelectItem>
                  <SelectItem value="in_progress">En Progreso</SelectItem>
                  <SelectItem value="passed">Aprobadas</SelectItem>
                  <SelectItem value="failed">Rechazadas</SelectItem>
                  <SelectItem value="conditional">Condicionales</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  <SelectItem value="incoming">Recepcion</SelectItem>
                  <SelectItem value="in_process">En Proceso</SelectItem>
                  <SelectItem value="final">Final</SelectItem>
                  <SelectItem value="random">Aleatoria</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
              </div>
            ) : filteredInspections && filteredInspections.length > 0 ? (
              <div className="space-y-4">
                {filteredInspections.map((inspection) => (
                  <Card
                    key={inspection.id}
                    className={cn(
                      "cursor-pointer hover:shadow-lg transition-all",
                      inspection.status === "failed" && "border-l-4 border-l-red-500",
                      inspection.status === "passed" && "border-l-4 border-l-green-500",
                      inspection.status === "in_progress" && "border-l-4 border-l-blue-500"
                    )}
                    onClick={() => setViewInspectionId(inspection.id)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-lg">
                              {inspection.work_orders?.product_name || "Sin Producto"}
                            </h3>
                            {getStatusBadge(inspection.status)}
                            <Badge variant="outline">{getTypeLabel(inspection.inspection_type)}</Badge>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">OT:</span>{" "}
                              <span className="font-medium">
                                {inspection.work_orders?.order_number || "-"}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Lote:</span>{" "}
                              <span className="font-medium">
                                {inspection.production_batches?.batch_number || "-"}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Inspector:</span>{" "}
                              <span className="font-medium">
                                {inspection.profiles?.full_name || inspection.profiles?.email || "-"}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Fecha:</span>{" "}
                              <span className="font-medium">
                                {format(new Date(inspection.inspection_date), "dd/MM/yyyy", { locale })}
                              </span>
                            </div>
                          </div>
                          {inspection.defects_found > 0 && (
                            <div className="mt-3 flex items-center gap-2 text-sm text-yellow-600">
                              <AlertTriangle className="w-4 h-4" />
                              {inspection.defects_found} defecto(s) encontrado(s)
                            </div>
                          )}
                        </div>
                        <Button variant="ghost" size="icon">
                          <Eye className="w-5 h-5" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <ClipboardCheck className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-2">
                    {searchTerm || statusFilter !== "all" || typeFilter !== "all"
                      ? "No se encontraron inspecciones con los filtros seleccionados"
                      : "No hay inspecciones registradas"}
                  </p>
                  {canManage && (
                    <Button onClick={() => setInspectionDialogOpen(true)} className="mt-4">
                      <Plus className="w-4 h-4 mr-2" />
                      Crear Primera Inspeccion
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="ncr" className="mt-6">
            <NCRList canManage={canManage} />
          </TabsContent>

          <TabsContent value="metrics" className="mt-6">
            <QualityMetricsDashboard />
          </TabsContent>
        </Tabs>
      </div>

      <QualityInspectionDialog
        open={inspectionDialogOpen}
        onOpenChange={setInspectionDialogOpen}
      />

      {selectedInspection && (
        <DefectFormDialog
          open={defectDialogOpen}
          onOpenChange={setDefectDialogOpen}
          inspectionId={selectedInspection}
        />
      )}
    </DashboardLayout>
  );
}
